import { isTruthy, useGraphMutation, useToast } from "@dashboard/library";
import { useTranslation } from "react-i18next";

import {
  DeleteNoteMutation,
  DeleteNoteMutationVariables,
} from "@/graphql/generated/graphql";
import { DeleteNote } from "@/graphql/mutations/DeleteNote";
import { GetTagsAndNotes } from "@/graphql/queries/GetTagsAndNotes";
import { useTrack } from "@/hooks/useTrack/useTrack";
import { usePathParams } from "@/states/path_param/path_params";
import { useCall } from "../useCall/useCall";

export function useDeleteNote() {
  const { t } = useTranslation();
  const { track } = useTrack();

  const { callId } = usePathParams();
  const { show } = useToast();
  const { call } = useCall();

  const [deleteMutation] = useGraphMutation(DeleteNote, {
    onCompleted() {
      show({
        message: t("note_deleted"),
        variant: "success",
      });
      track("conversation_note_deleted", {
        call_id: call?.id,
      });
    },
    onError(e) {
      show({
        variant: "critical",
        message: e.message,
      });
    },
  });

  const deleteNote = ({ noteId }: DeleteNoteMutationVariables) => {
    const optimisticDeletion: DeleteNoteMutation = {
      deleteNote: {
        id: noteId,
      },
    };

    deleteMutation({
      variables: {
        noteId,
      },
      optimisticResponse: optimisticDeletion,
      update(cache) {
        const readTagsAndNotes = cache.readQuery({
          query: GetTagsAndNotes,
          variables: {
            callId,
          },
        });

        cache.writeQuery({
          query: GetTagsAndNotes,
          data: {
            getTagsAndNotes: {
              __typename: "TagsAndNotes",
              tags: readTagsAndNotes?.getTagsAndNotes?.tags ?? [],
              notes:
                readTagsAndNotes?.getTagsAndNotes?.notes
                  ?.filter(isTruthy)
                  .filter((note) => note.id !== noteId) ?? [],
            },
          },
          variables: {
            callId,
          },
        });
      },
    });
  };

  return {
    deleteNote,
  };
}
